/**
 * Simulation engine. Steps the boat forward in scenario time, applying STW
 * along the current heading, the scenario tide (set + rate) as a vector, and
 * a leeway offset from the wind.
 *
 * It's a teaching simulator, not a high-fidelity ship model — but it gets the
 * tide/leeway physics right, so the water track and ground track diverge in
 * the way the chartwork says they should.
 */
import { rhumbDestination } from '@/nav';
import { sampleTide, type TideCycle } from '@/environment';

export interface SimStepInput {
  position: [number, number];
  /** Heading through water, degrees true. */
  headingDeg: number;
  /** Speed through water, knots. */
  stwKn: number;
  /** Optional leeway angle in degrees (water track offset from heading). */
  leewayDeg?: number;
  /** Scenario tide. */
  tide: TideCycle;
  /** Time at the start of the step, hours since t=0. */
  tHour: number;
  /** Length of the step in real seconds. */
  dtSec: number;
  /** Time-acceleration factor (1 = real time, 5 = 5×, …). */
  rate: number;
}

export interface SimStepOutput {
  position: [number, number];
  /** Time at the end of the step, hours since t=0. */
  tHour: number;
  /** COG produced by the heading + tide combination. */
  cogDeg: number;
  /** SOG produced by the heading + tide combination. */
  sogKn: number;
}

export function simStep(input: SimStepInput): SimStepOutput {
  const dtHour = (input.dtSec * input.rate) / 3600;
  // Sample tide at the midpoint of the step for better integration.
  const midT = input.tHour + dtHour / 2;
  const tide = sampleTide(input.tide, midT);

  // 1. Walk through the water along the water track.
  const waterTrackDeg = (input.headingDeg + (input.leewayDeg ?? 0) + 360) % 360;
  const throughWater = input.stwKn * dtHour;
  const afterWater = rhumbDestination(input.position, waterTrackDeg, throughWater);

  // 2. Apply the tidal vector for the same interval.
  const tideDistance = tide.rateKn * dtHour;
  const newPosition = rhumbDestination(afterWater, tide.setDeg, tideDistance);

  // 3. Compute COG/SOG from the actual displacement.
  const dxNm = (newPosition[0] - input.position[0]) * 60 * Math.cos((input.position[1] * Math.PI) / 180);
  const dyNm = (newPosition[1] - input.position[1]) * 60;
  const groundNm = Math.hypot(dxNm, dyNm);
  const sogKn = dtHour > 0 ? groundNm / dtHour : 0;
  const cogDeg = ((Math.atan2(dxNm, dyNm) * 180) / Math.PI + 360) % 360;

  return {
    position: newPosition,
    tHour: input.tHour + dtHour,
    cogDeg,
    sogKn,
  };
}

/**
 * Estimate leeway angle from wind direction relative to heading.
 *
 * Very rough teaching rule: leeway peaks when the wind is on the beam, falls to
 * zero on a dead head or run, and the sign depends on which side the wind hits.
 * We assume up to 5° at 20 kn on the beam, scaling linearly with wind speed.
 *
 * Wind from starboard pushes the boat to port, so the water track is to PORT of
 * the heading. On our convention (water track = heading + leeway) that's a
 * negative leeway angle.
 */
export function leewayFromWind(headingDeg: number, windFromDeg: number, windKn: number): number {
  if (windKn <= 0) return 0;
  let rel = ((windFromDeg - headingDeg + 540) % 360) - 180;
  if (rel <= -180) rel += 360;
  const beamFactor = Math.sin((rel * Math.PI) / 180); // -1..+1
  const speedFactor = Math.min(1, windKn / 20);
  const maxLeeway = 5; // degrees
  return -maxLeeway * beamFactor * speedFactor;
}

